import classNames from 'classnames/bind';
import { SelectAirportContext } from 'common/autocomplete/SelectAirportContext';
import Error from 'common/error';
import Loading from 'common/loading';
import { Modal, ModalFuncContext, ModalPosition } from 'contexts/modalFunc';
import { TripType, useSearchParamsContext } from 'contexts/searchParams';
import useAirportDetailList from 'hooks/useAirportDetailList';
import { useSearchHistories } from 'hooks/useHistory';
import React, { useCallback, useContext, useMemo } from 'react';
import SearchEverywhere from './SearchEverywhere';
import styles from './index.module.scss';

const cx = classNames.bind(styles);
const SEARCH_HISTORY_COUNT = 6;

interface PopularAirport {
  name: string;
  cities: {
    name: string;
    code: string;
    clickCode: string;
    isEverywhere?: boolean;
  }[];
  clickCode?: string;
  continentId?: string;
  isEverywhere?: boolean;
  type?: string;
}

const popularDepartureAirports: PopularAirport[] = [
  {
    name: '국내 출발',
    isEverywhere: true,
    cities: [
      {
        name: '서울',
        code: 'SEL',
        clickCode: 'krsel',
        isEverywhere: true,
      },
      {
        name: '인천',
        code: 'ICN',
        clickCode: 'kricn',
        isEverywhere: true,
      },
      {
        name: '김포',
        code: 'GMP',
        clickCode: 'krgmp',
        isEverywhere: true,
      },
      {
        name: '부산',
        code: 'PUS',
        clickCode: 'krpus',
        isEverywhere: true,
      },
      {
        name: '제주',
        code: 'CJU',
        clickCode: 'krcju',
        isEverywhere: true,
      },
      {
        name: '청주',
        code: 'CJJ',
        clickCode: 'krcjj',
        isEverywhere: true,
      },
      {
        name: '대구',
        code: 'TAE',
        clickCode: 'krtae',
        isEverywhere: true,
      },
      {
        name: '무안',
        code: 'MWX',
        clickCode: 'krmwx',
        isEverywhere: true,
      },
      {
        name: '광주',
        code: 'KWJ',
        clickCode: 'krkwj',
      },
      {
        name: '여수',
        code: 'RSU',
        clickCode: 'krrsu',
      },
      {
        name: '군산',
        code: 'KUV',
        clickCode: 'krkuv',
      },
      {
        name: '울산',
        code: 'USN',
        clickCode: 'krusn',
      },
      {
        name: '포항',
        code: 'KPO',
        clickCode: 'krkpo',
      },
      {
        name: '원주',
        code: 'WJU',
        clickCode: 'krwju',
      },
      {
        name: '사천',
        code: 'HIN',
        clickCode: 'krhin',
      },
      {
        name: '양양',
        code: 'YNY',
        clickCode: 'kryny',
      },
    ],
  },
  {
    name: '해외 출발',
    cities: [
      {
        name: '도쿄',
        code: 'TYO',
        clickCode: 'gltyo',
      },
      {
        name: '오사카',
        code: 'OSA',
        clickCode: 'glosa',
      },
      {
        name: '후쿠오카',
        code: 'FUK',
        clickCode: 'glfuk',
      },
      {
        name: '방콕',
        code: 'BKK',
        clickCode: 'glbkk',
      },
      {
        name: '호치민',
        code: 'SGN',
        clickCode: 'glsgn',
      },
      {
        name: '다낭',
        code: 'DAD',
        clickCode: 'gldad',
      },
    ],
  },
];

const popularArrivalAirports: PopularAirport[] = [
  {
    name: '국내',
    type: 'domestic',
    cities: [
      {
        name: '제주',
        code: 'CJU',
        clickCode: 'krcju',
      },
      {
        name: '김포',
        code: 'GMP',
        clickCode: 'krgmp',
      },
      {
        name: '부산',
        code: 'PUS',
        clickCode: 'krpus',
      },
    ],
  },
  {
    name: '일본',
    continentId: '3',
    type: 'country',
    clickCode: 'jp',
    cities: [
      {
        name: '도쿄',
        code: 'TYO',
        clickCode: 'jptyo',
      },
      {
        name: '오사카',
        code: 'OSA',
        clickCode: 'jposa',
      },
      {
        name: '후쿠오카',
        code: 'FUK',
        clickCode: 'jpfuk',
      },
      {
        name: '삿포로',
        code: 'SPK',
        clickCode: 'jpspk',
      },
      {
        name: '오키나와',
        code: 'OKA',
        clickCode: 'jpoka',
      },
    ],
  },
  {
    name: '동남아시아',
    continentId: '1',
    type: 'city',
    clickCode: 'asean',
    cities: [
      {
        name: '나트랑',
        code: 'NHA',
        clickCode: 'aseannha',
      },
      {
        name: '다낭',
        code: 'DAD',
        clickCode: 'aseandad',
      },
      {
        name: '방콕',
        code: 'BKK',
        clickCode: 'aseanbkk',
      },
      {
        name: '마닐라',
        code: 'MNL',
        clickCode: 'aseanmnl',
      },
      {
        name: '세부',
        code: 'CEB',
        clickCode: 'aseanceb',
      },
      {
        name: '발리',
        code: 'DPS',
        clickCode: 'aseandps',
      },
    ],
  },
  {
    name: '유럽',
    continentId: '5',
    type: 'city',
    clickCode: 'eu',
    cities: [
      {
        name: '파리',
        code: 'PAR',
        clickCode: 'eupar',
      },
      {
        name: '런던',
        code: 'LON',
        clickCode: 'eulon',
      },
      {
        name: '로마',
        code: 'ROM',
        clickCode: 'eurom',
      },
      {
        name: '바르셀로나',
        code: 'BCN',
        clickCode: 'eubcn',
      },
      {
        name: '취리히',
        code: 'ZRH',
        clickCode: 'euzrh',
      },
      {
        name: '프랑크푸르트',
        code: 'FRA',
        clickCode: 'eufra',
      },
      {
        name: '이스탄불',
        code: 'IST',
        clickCode: 'euist',
      },
    ],
  },
  {
    name: '아시아',
    continentId: '2,7',
    type: 'city',
    clickCode: 'eastasia',
    cities: [
      {
        name: '타이베이',
        code: 'TPE',
        clickCode: 'eastasiatpe',
      },
      {
        name: '홍콩',
        code: 'HKG',
        clickCode: 'eastasiahkg',
      },
      {
        name: '마카오',
        code: 'MFM',
        clickCode: 'eastasiamfm',
      },
      {
        name: '상하이',
        code: 'PVG',
        clickCode: 'eastasiapvg',
      },
      {
        name: '울란바토르',
        code: 'UBN',
        clickCode: 'eastasiaubn',
      },
    ],
  },
  {
    name: '미주',
    continentId: '4',
    type: 'city',
    clickCode: 'na',
    cities: [
      {
        name: 'LA',
        code: 'LAX',
        clickCode: 'nalax',
      },
      {
        name: '하와이',
        code: 'HNL',
        clickCode: 'nahnl',
      },
      {
        name: '뉴욕',
        code: 'NYC',
        clickCode: 'nanyc',
      },
      {
        name: '밴쿠버',
        code: 'YVR',
        clickCode: 'nayvr',
      },
    ],
  },
  {
    name: '남태평양',
    continentId: '6',
    type: 'city',
    clickCode: 'oc',
    cities: [
      {
        name: '괌',
        code: 'GUM',
        clickCode: 'ocgum',
      },
      {
        name: '사이판',
        code: 'SPN',
        clickCode: 'ocspn',
      },
      {
        name: '시드니',
        code: 'SYD',
        clickCode: 'ocsyd',
      },
    ],
  },
];

interface IProps {
  forDeparture: boolean;
  isEverywhere: boolean;
}

const PopularCities = ({ forDeparture, isEverywhere }: IProps) => {
  const setModal = useContext(ModalFuncContext);
  const handleClick = useContext(SelectAirportContext);
  const { setDepartureCity } = useSearchParamsContext();
  const handleCityClick = (code: string, name: string) => {
    setDepartureCity({ code, name, isDomestic: true });
    setModal({ type: Modal.NULL, position: ModalPosition.MAIN });
  };
  const handleEntireClick = (continentId?: string) => {
    location.href = `/flights/everywhere/monthly/SEL${continentId ? `?continents=${continentId}` : ''}`;
  };

  const [popularAirports, popularAirportCodeList] = useMemo(() => {
    const popularAirports = forDeparture
      ? popularDepartureAirports.filter(
          airport => !isEverywhere || airport?.isEverywhere,
        )
      : popularArrivalAirports;
    const popularAirportCodeList = popularAirports.flatMap(airport =>
      airport.cities.map(city => city.code),
    );
    return [popularAirports, popularAirportCodeList];
  }, [forDeparture, isEverywhere]);

  const { data } = useSearchHistories(SEARCH_HISTORY_COUNT);

  const recentlySearched = useMemo(
    () =>
      data?.searchHistories
        ?.filter(history => history.tripList && history.tripList.length)
        .flatMap(history =>
          history.trip === TripType.MD
            ? history.tripList.map(trip => ({
                code: forDeparture
                  ? trip.departureCityCode
                  : trip.destinationCityCode,
                name: forDeparture
                  ? trip.departureCityName
                  : trip.destinationCityName,
              }))
            : {
                code: forDeparture
                  ? history.tripList[0].departureCityCode
                  : history.tripList[0].destinationCityCode,
                name: forDeparture
                  ? history.tripList[0].departureCityName
                  : history.tripList[0].destinationCityName,
              },
        )
        .reduce<{ code: string; name: string }[]>((acc, cur) => {
          if (!acc.some(item => item.code === cur.code)) {
            acc.push(cur);
          }
          return acc;
        }, []) ?? [],
    [data?.searchHistories, forDeparture],
  );

  const {
    data: popularAirportDetail,
    loading,
    error,
  } = useAirportDetailList(
    popularAirportCodeList.concat(recentlySearched.map(({ code }) => code)),
  );

  const getDetailFromAirport = useCallback(
    (code: string) => {
      const airport = popularAirportDetail?.airportDetailList?.find(
        ({ iataCode }) => iataCode === code,
      );
      return airport;
    },
    [popularAirportDetail?.airportDetailList],
  );

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error error={error} />;
  }

  return (
    <div className={cx('PopularCities')}>
      {!(forDeparture && isEverywhere) && (
        <b className={cx('title')}>주요 도시</b>
      )}
      {!forDeparture && <SearchEverywhere />}
      <div className={cx('cities_area')}>
        {recentlySearched?.length > 0 && !(forDeparture && isEverywhere) && (
          <>
            <div className={cx('header')}>
              <span className={cx('category')}>최근 검색 도시</span>
            </div>
            <ul className={cx('city_list')}>
              {recentlySearched.map(({ code, name }, index) => (
                <li className={cx('list_item')} key={code}>
                  <button
                    className={cx('button')}
                    type="button"
                    data-event-nlog="click"
                    data-event-area-code={`${forDeparture ? 'dep' : 'arr'}.recentcity${index + 1}`}
                    onClick={() =>
                      handleClick({
                        code,
                        name,
                        isDomestic:
                          getDetailFromAirport(code)?.isDomestic ?? false,
                        cityCode: code,
                      })
                    }
                  >
                    <span className={cx('name')}>{name}</span>
                    <i className={cx('code')}>{code}</i>
                    {!forDeparture && getDetailFromAirport(code)?.event && (
                      <i className={cx('event')}>
                        <span className={cx('txt')}>이벤트</span>
                      </i>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {popularAirports.map(
          ({ name, type, cities, clickCode, continentId }, index) => (
            <React.Fragment key={index}>
              <div className={cx('header')}>
                <span className={cx('category')}>{name}</span>
                {forDeparture && isEverywhere && (
                  <span className={cx('sub')}>
                    모든 곳 검색 출발지는 국내 주요 공항만 선택 가능
                  </span>
                )}
              </div>
              <ul className={cx('city_list')}>
                {!forDeparture && type !== 'domestic' && (
                  <li className={cx('list_item')}>
                    <button
                      className={cx('button', type && `type_${type}_all`)}
                      type="button"
                      data-event-nlog={clickCode ? 'click' : undefined}
                      data-event-area-code={
                        clickCode ? `arr.${clickCode}all` : undefined
                      }
                      onClick={() => handleEntireClick(continentId)}
                    >
                      전체
                    </button>
                  </li>
                )}
                {cities
                  .filter(
                    city =>
                      !forDeparture || !isEverywhere || city?.isEverywhere,
                  )
                  .map(city => (
                    <li key={city.code}>
                      <button
                        className={cx('button')}
                        type="button"
                        data-event-nlog="click"
                        data-event-area-code={`${forDeparture ? 'dep' : 'arr'}.${city.clickCode}`}
                        onClick={() =>
                          forDeparture && isEverywhere
                            ? handleCityClick(city.code, city.name)
                            : handleClick({
                                code: city.code,
                                name: city.name,
                                isDomestic:
                                  getDetailFromAirport(city.code)?.isDomestic ??
                                  false,
                                cityCode: city.code,
                              })
                        }
                      >
                        {city.name}
                        {!forDeparture &&
                          getDetailFromAirport(city.code)?.event && (
                            <i className={cx('event')}>
                              <span className={cx('txt')}>이벤트</span>
                            </i>
                          )}
                      </button>
                    </li>
                  ))}
              </ul>
            </React.Fragment>
          ),
        )}
      </div>
    </div>
  );
};

export default PopularCities;
